import { SectionSummary } from '@/components/WorkspacePresentation';
/**
 * Opportunities and recommendations.
 *
 * The point of this screen is that every recommendation shows *why* it exists:
 * the evidence, and the individual scoring components behind its priority.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { CheckCircleIcon } from '@/components/icons';
import { SupplyValuesForm } from '@/features/opportunities/SupplyValuesForm';
import { isDraftable, isWritable } from '@/features/opportunities/writableActions';
import { aiApi, recommendationApi } from '@/api/endpoints';
import { useAuth } from '@/app/auth';
import { Permission } from '@/app/permissions';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  SeverityBadge,
  UrlText,
} from '@/components/ui';
import {
  formatPercent,
} from '@/utils/format';
import type { RecommendationDetail, RecommendationSummary } from '@/types/api';

export function OpportunitiesPage() {
  const { siteId = '' } = useParams();
  const [created, setCreated] = useState(0);

  const aiQuery = useQuery({ queryKey: ['ai-status'], queryFn: () => aiApi.status() });

  const query = useQuery({
    queryKey: ['recommendations', siteId],
    queryFn: () => recommendationApi.list(siteId, { page_size: 50, status: 'proposed' }),
  });

  return (
    <>
      <PageHeader
        title="Opportunities"
        description="Ranked by expected impact, confidence, business value and traffic potential, divided by effort. Every score is shown in full."
      />

      {query.data && <SectionSummary items={[
        { label: 'Proposed opportunities', value: query.data.total, detail: 'Ready to explore' },
        { label: 'Recommendations in view', value: query.data.items.length, detail: 'Evidence behind every priority' },
        { label: 'Actions created here', value: created, detail: 'Awaiting your review' },
      ]} />}

      {/* Lives here rather than in the card: creating an action moves the
          recommendation out of "proposed", so the card unmounts immediately
          and any confirmation inside it would vanish with it. */}
      {created > 0 ? (
        <div className="mb-5">
          <Callout
            tone="success"
            title={`${created} action${created === 1 ? '' : 's'} created`}
            icon={<CheckCircleIcon className="h-5 w-5" />}
            action={
              <Link
                to="/actions"
                className="inline-flex rounded-lg bg-surface px-3 py-1.5 text-sm font-medium whitespace-nowrap text-brand shadow-card hover:bg-brand-subtle"
              >
                Review
              </Link>
            }
          >
            Waiting for your approval in the action center. Nothing has been applied to your site.
          </Callout>
        </div>
      ) : null}

      {query.isLoading ? <LoadingState label="Loading recommendations" /> : null}
      {query.isError ? (
        <ErrorState message={(query.error as Error).message} onRetry={() => void query.refetch()} />
      ) : null}

      {query.data && query.data.items.length === 0 ? (
        <EmptyState
          title="No open recommendations"
          description="Run an audit to generate prioritised recommendations from your crawl and search data."
        />
      ) : null}

      <div className="space-y-4">
        {query.data?.items.map((recommendation) => (
          <RecommendationCard
            key={recommendation.id}
            summary={recommendation}
            aiEnabled={aiQuery.data?.enabled ?? false}
            onCreated={(count) => setCreated(count)}
          />
        ))}
      </div>
    </>
  );
}

function RecommendationCard({
  summary,
  aiEnabled,
  onCreated,
}: {
  summary: RecommendationSummary;
  aiEnabled: boolean;
  onCreated: (count: number) => void;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [writing, setWriting] = useState(false);

  // The form needs the affected URLs, which only the detail carries.
  const detailQuery = useQuery({
    queryKey: ['recommendation', summary.id],
    queryFn: () => recommendationApi.get(summary.id),
    enabled: expanded || writing,
  });

  const dismissMutation = useMutation({
    mutationFn: () => recommendationApi.dismiss(summary.id, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recommendations'] }),
  });

  const createActionsMutation = useMutation({
    mutationFn: (payloads?: Record<string, unknown>[]) =>
      recommendationApi.createActions(summary.id, payloads ? { payloads } : {}),
    onSuccess: async (actions) => {
      setWriting(false);
      onCreated(actions.length);
      await queryClient.invalidateQueries({ queryKey: ['recommendations'] });
      await queryClient.invalidateQueries({ queryKey: ['actions'] });
    },
  });

  // can_auto_execute only says the recommendation maps to an action type; the
  // values may still be missing, which is what used to make the button fail.
  const needsValues = summary.can_auto_execute && !summary.has_proposed_values;
  // Offer a button only where an action can actually be produced. Otherwise say
  // plainly that the change is a manual one, rather than inviting a click that
  // ends in "does not carry enough detail".
  const canProduce = summary.has_proposed_values || isWritable(summary.proposed_action_type);
  // Values are written for you by default; typing them is the fallback, not
  // the expectation.
  const canDraft = aiEnabled && isDraftable(summary.proposed_action_type);

  const draftMutation = useMutation({
    mutationFn: () => recommendationApi.draftValues(summary.id),
  });
  const drafts = Object.fromEntries(
    (draftMutation.data?.drafts ?? []).map((draft) => [draft.url, draft]),
  );

  function openWriting() {
    setWriting(true);
    // Draft straight away rather than making the reviewer ask twice.
    if (canDraft && !draftMutation.data && !draftMutation.isPending) draftMutation.mutate();
  }

  return (
    <Card>
      {/* One row rather than three stacked bands: the score, what it is, and
          what you can do about it all sit on the same line, so a queue of these
          can be scanned vertically instead of read card by card. */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="opportunity-priority flex shrink-0 flex-col items-center justify-center rounded-lg bg-surface-muted px-3 py-2 lg:w-20">
          <span className="tnum text-2xl leading-none font-semibold text-ink">
            {Math.round(summary.priority_score)}
          </span>
          <span className="mt-1 text-[10px] tracking-wide text-ink-subtle uppercase">
            priority
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-ink">{summary.title}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <SeverityBadge severity={summary.severity} />
            <Badge tone="neutral">{summary.category.replace(/_/g, ' ')}</Badge>
            <Badge tone="neutral">impact: {summary.impact_band}</Badge>
            <Badge tone="neutral">effort: {summary.effort_band}</Badge>
            <Badge tone="neutral">risk: {summary.risk_band}</Badge>
            <Badge tone="neutral">confidence: {Math.round(summary.confidence * 100)}%</Badge>
            {summary.is_ai_generated ? <Badge tone="brand">AI</Badge> : null}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setExpanded((current) => !current)}>
            {expanded ? 'Hide detail' : 'Why this matters'}
          </Button>
          {summary.can_auto_execute && can(Permission.ACTION_CREATE) && !canProduce ? (
            <span
              className="text-xs text-ink-subtle"
              title="This change needs a link target, an image or structured data — details a text field cannot carry."
            >
              manual change
            </span>
          ) : null}
          {summary.can_auto_execute && can(Permission.ACTION_CREATE) && canProduce ? (
            <Button
              size="sm"
              variant="primary"
              loading={createActionsMutation.isPending && !writing}
              onClick={() =>
                needsValues
                  ? writing
                    ? setWriting(false)
                    : openWriting()
                  : createActionsMutation.mutate(undefined)
              }
              title={
                needsValues
                  ? 'This finding needs the replacement text before it can be executed'
                  : undefined
              }
            >
              {needsValues
                ? writing
                  ? 'Cancel'
                  : canDraft
                    ? 'Write with AI'
                    : 'Write values'
                : 'Create action'}
            </Button>
          ) : null}
          {can(Permission.ISSUE_UPDATE) ? (
            <Button
              size="sm"
              variant="ghost"
              loading={dismissMutation.isPending}
              onClick={() => dismissMutation.mutate()}
            >
              Dismiss
            </Button>
          ) : null}
        </div>
      </div>

      {writing ? (
        <div className="mt-4 border-t border-border-subtle pt-4">
          {detailQuery.isLoading ? (
            <LoadingState label="Loading the affected pages" />
          ) : (
            <SupplyValuesForm
              actionType={summary.proposed_action_type}
              urls={(detailQuery.data?.affected_urls ?? [])
                .map((entry) => (entry as { url?: string }).url)
                .filter((url): url is string => Boolean(url))}
              drafts={drafts}
              draftNotes={
                draftMutation.isError
                  ? draftMutation.error instanceof ApiError
                    ? draftMutation.error.message
                    : 'The values could not be written automatically. You can type them instead.'
                  : draftMutation.data?.notes
              }
              drafting={draftMutation.isPending}
              onRedraft={canDraft ? () => draftMutation.mutate() : undefined}
              pending={createActionsMutation.isPending}
              onCancel={() => setWriting(false)}
              onSubmit={(payloads) => createActionsMutation.mutate(payloads)}
            />
          )}
        </div>
      ) : null}

      {createActionsMutation.isError ? (
        <div className="mt-3">
          <Callout tone="warning" title="Could not create an action">
            {createActionsMutation.error instanceof ApiError
              ? createActionsMutation.error.message
              : 'The action could not be created.'}
          </Callout>
        </div>
      ) : null}

      {expanded ? (
        <div className="mt-4 border-t border-border-subtle pt-4">
          {detailQuery.isLoading ? <LoadingState label="Loading evidence" /> : null}
          {detailQuery.data ? <RecommendationDetailView detail={detailQuery.data} /> : null}
        </div>
      ) : null}
    </Card>
  );
}

function RecommendationDetailView({ detail }: { detail: RecommendationDetail }) {
  return (
    <div className="space-y-5">
      <section>
        <h4 className="text-xs font-medium tracking-wide text-ink-subtle uppercase">Problem</h4>
        <p className="mt-1.5 text-sm text-ink-muted">{detail.problem}</p>
      </section>

      <section>
        <h4 className="text-xs font-medium tracking-wide text-ink-subtle uppercase">
          Recommended change
        </h4>
        <p className="mt-1.5 text-sm text-ink-muted">{detail.recommended_change}</p>
      </section>

      {detail.expected_impact ? (
        <section>
          <h4 className="text-xs font-medium tracking-wide text-ink-subtle uppercase">
            Expected impact
          </h4>
          <p className="mt-1.5 text-sm text-ink-muted">{detail.expected_impact}</p>
        </section>
      ) : null}

      {detail.evidence_items.length > 0 ? (
        <section>
          <h4 className="text-xs font-medium tracking-wide text-ink-subtle uppercase">Evidence</h4>
          <ul className="mt-2 space-y-2">
            {detail.evidence_items.map((item) => (
              <li key={item.id} className="rounded-md bg-surface-muted p-3">
                <p className="text-sm font-medium text-ink">{item.label}</p>
                {item.value ? <p className="mt-0.5 text-sm text-ink-muted">{item.value}</p> : null}
                {item.source ? (
                  <p className="mt-1 text-xs text-ink-subtle">Source: {item.source}</p>
                ) : null}
                {item.kind === 'deterministic_check' && item.label.includes('components') ? (
                  <ScoreBreakdown data={item.data} />
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {detail.affected_urls.length > 0 ? (
        <section>
          <h4 className="text-xs font-medium tracking-wide text-ink-subtle uppercase">
            Affected URLs
          </h4>
          <ul className="mt-2 space-y-1">
            {detail.affected_urls.slice(0, 15).map((entry, index) => (
              <li key={`${entry.url}-${index}`}>
                <UrlText url={entry.url} />
                {entry.detail ? (
                  <span className="text-xs text-ink-subtle">{entry.detail}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** The individual scoring components, so a priority is never a black box. */
function ScoreBreakdown({ data }: { data: Record<string, unknown> }) {
  const components: [string, number][] = (
    [
      'impact',
      'confidence',
      'business_value',
      'traffic_potential',
      'effort',
      'risk',
    ] as const
  )
    .map((key) => [key, Number(data[key] ?? 0)] as [string, number])
    .filter(([, value]) => Number.isFinite(value));

  const notes = Array.isArray(data.notes) ? (data.notes as string[]) : [];

  return (
    <div className="mt-3 space-y-2">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
        {components.map(([key, value]) => (
          <div key={key}>
            <p className="text-[10px] tracking-wide text-ink-subtle uppercase">
              {key.replace(/_/g, ' ')}
            </p>
            <div className="mt-0.5 flex items-center gap-2">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-border-subtle">
                <div
                  className={
                    key === 'effort' || key === 'risk'
                      ? 'h-full rounded-full bg-warning'
                      : 'h-full rounded-full bg-brand'
                  }
                  style={{ width: `${Math.min(100, value * 100)}%` }}
                />
              </div>
              <span className="tnum text-[10px] text-ink-muted">{formatPercent(value, 0)}</span>
            </div>
          </div>
        ))}
      </div>
      {notes.length > 0 ? (
        <ul className="space-y-0.5 text-[11px] text-ink-subtle">
          {notes.map((note) => (
            <li key={note}>· {note}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
